/**
 * ProfilePage — User profile and settings
 *
 * Displays user info, level, stats, sign-out, and
 * an option to reset progress with confirmation.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { LogOut, RotateCcw, User, Cloud, Shield, Bookmark } from 'lucide-react';

import { useAuth } from '../context/AuthContext';
import { useGamification } from '../context/GamificationContext';
import { useProgress } from '../context/ProgressContext';
import { db, isDemoMode } from '../services/firebase';
import {
  hubSyncAvailable,
  discardPendingPatches,
  savePatchNow,
} from '../services/hubAppState';
import { doc, deleteDoc } from 'firebase/firestore';
import {
  hasAnalyticsPreference,
  setAnalyticsChoice,
} from '../services/analytics';
import Card from '../components/shared/Card';
import Button from '../components/shared/Button';

const PRIVACY_URL = 'https://divedeepdialogues.com/privacy';
const TERMS_URL = 'https://divedeepdialogues.com/terms';

const ProfilePage = () => {
  const navigate = useNavigate();
  const { currentUser, signOut, isAuthenticated } = useAuth();
  const { xp, level, daysVisited, resetProgress: resetGamification } = useGamification();
  const { moduleProgress } = useProgress();

  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [analyticsOn, setAnalyticsOn] = useState(hasAnalyticsPreference);
  const location = useLocation();

  // The consent notice links here as /profile#your-data. ScrollToTop resets
  // the scroll on every route change, so bring the card into view afterwards.
  useEffect(() => {
    if (location.hash !== '#your-data') return;
    const timer = setTimeout(() => {
      document.getElementById('your-data')?.scrollIntoView({ block: 'start' });
    }, 100);
    return () => clearTimeout(timer);
  }, [location.hash]);

  const handleAnalyticsToggle = () => {
    const next = !analyticsOn;
    setAnalyticsOn(next);
    // Live at once: saying yes loads Google Analytics now, saying no stops it.
    setAnalyticsChoice(next);
  };

  const completedModules = Object.values(moduleProgress).filter(
    (m) => m.status === 'completed'
  ).length;

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/signin');
    } catch {
      // Error handled in AuthContext
    }
  };

  const handleResetProgress = async () => {
    resetGamification();
    // Clear local progress storage. Toolkit journal entries (fyv-tool-*) are
    // deliberately KEPT — they are the user's own writing, not progress.
    localStorage.removeItem('fyvProgress');
    localStorage.removeItem('fyvGamification');
    localStorage.removeItem('fyvCharacterState');
    localStorage.removeItem('fyvCourtyardState');
    localStorage.removeItem('fyvYourTurn'); // answers given inside the evenings

    // Clear the synced copies too, or the erased progress re-hydrates on
    // the next login: hub thin layer first, then the legacy own-project doc.
    if (isAuthenticated && !isDemoMode) {
      if (hubSyncAvailable()) {
        discardPendingPatches(); // a queued patch must not re-save what was just erased
        await savePatchNow({ progress: null }).catch(() => {});
      }
      if (db && currentUser?.uid) {
        try {
          await deleteDoc(doc(db, 'users', currentUser.uid, 'progress', 'data'));
        } catch {
          // legacy doc may not exist or be reachable — nothing to clear
        }
      }
    }

    setShowResetConfirm(false);
    window.location.reload();
  };

  return (
    <>
      <Helmet>
        <title>Profile | Finding Your Voice</title>
      </Helmet>

      <div className="max-w-2xl mx-auto px-4 py-10 space-y-8">
        <motion.h1
          className="font-display text-3xl font-bold text-courtyard-stone text-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          Your Profile
        </motion.h1>

        {/* User Info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Card className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-full bg-courtyard-gold/10 flex items-center justify-center">
                <User className="w-7 h-7 text-courtyard-gold-ink" aria-hidden="true" />
              </div>
              <div>
                <h2 className="font-display text-xl font-bold text-courtyard-stone">
                  {currentUser?.displayName || 'Guest'}
                </h2>
                <p className="text-sm text-courtyard-stone/80">
                  {currentUser?.email || 'No account'}
                </p>
              </div>
            </div>
          </Card>
        </motion.div>

        {/* Guest Sign-Up Prompt */}
        {!isAuthenticated && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
          >
            <Card className="p-6 bg-courtyard-gold/5 border-courtyard-gold/20">
              <h3 className="font-display font-bold text-courtyard-stone mb-2">
                Create a free account
              </h3>
              <p className="text-sm text-courtyard-stone/80 mb-4">
                You are using Finding Your Voice as a guest, so your progress
                is saved in this browser and nowhere else. A free Dive Deep
                Dialogues account gives you:
              </p>
              <ul className="space-y-2 mb-5">
                {[
                  { icon: Cloud, text: 'Your progress on any device you sign in on' },
                  { icon: Shield, text: 'A copy of your toolkit and journal entries if this browser is cleared' },
                  { icon: Bookmark, text: 'One sign-in for every Dive Deep Dialogues app' },
                ].map(({ icon: Icon, text }) => (
                  <li key={text} className="flex items-start gap-2 text-sm text-courtyard-stone/80">
                    <Icon className="w-4 h-4 text-courtyard-clay mt-0.5 flex-shrink-0" aria-hidden="true" />
                    {text}
                  </li>
                ))}
              </ul>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link to="/signup" className="courtyard-btn-primary flex-1 text-center">
                  Create account
                </Link>
                <Link to="/signin" className="courtyard-btn-secondary flex-1 text-center">
                  Sign in
                </Link>
              </div>
            </Card>
          </motion.div>
        )}

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <div>
            <h3 className="font-display font-bold text-courtyard-stone mb-4">
              Stats
            </h3>
            <div className="grid grid-cols-2 gap-3">
              <Card className="p-4 text-center bg-courtyard-gold/8 border-courtyard-gold/20">
                <p className="text-2xl font-bold text-courtyard-stone">{xp}</p>
                <p className="text-sm text-courtyard-gold-ink">Total XP</p>
              </Card>
              <Card className="p-4 text-center bg-philemon-bg/40 border-courtyard-olive/20">
                <p className="text-2xl font-bold text-courtyard-stone">
                  {level.number}
                </p>
                <p className="text-sm text-courtyard-olive-dark">{level.name}</p>
              </Card>
              <Card className="p-4 text-center bg-theron-bg/40 border-courtyard-terracotta/20">
                {/* Cumulative days, as on the Achievements page. A consecutive-day
                    streak punishes exactly the anxious reader this app is for. */}
                <p className="text-2xl font-bold text-courtyard-stone">{daysVisited}</p>
                <p className="text-sm text-courtyard-clay">Days in the Courtyard</p>
              </Card>
              <Card className="p-4 text-center bg-xenia-bg/40 border-courtyard-aegean/20">
                <p className="text-2xl font-bold text-courtyard-stone">
                  {completedModules}/8
                </p>
                <p className="text-sm text-courtyard-aegean-dark">Evenings</p>
              </Card>
            </div>
          </div>
        </motion.div>

        {/* Your data: where it lives, and the analytics switch */}
        <motion.div
          id="your-data"
          className="scroll-mt-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
        >
          <h3 className="font-display font-bold text-courtyard-stone mb-4">
            Your data
          </h3>
          <Card className="p-6 space-y-5">
            <div>
              <h4 className="font-bold text-courtyard-stone mb-1">Where it lives</h4>
              <p className="text-sm text-courtyard-stone/80 leading-relaxed">
                {isAuthenticated
                  ? 'Your progress, your assessment results and what you write in the tools are saved in this browser, and a copy is kept with your Dive Deep Dialogues account so it follows you between devices. Deleting that account deletes the copy.'
                  : 'Your progress, your assessment results and what you write in the tools are saved in this browser only. Nothing you write here is sent to us while you are a guest.'}
              </p>
              <p className="text-sm text-courtyard-stone/80 leading-relaxed mt-2">
                You can delete any saved tool or journal entry from that tool&rsquo;s history.
              </p>
            </div>

            <div className="border-t border-courtyard-stone/10 pt-5">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h4 id="analytics-switch-label" className="font-bold text-courtyard-stone mb-1">
                    Usage statistics
                  </h4>
                  <p id="analytics-switch-help" className="text-sm text-courtyard-stone/80 leading-relaxed">
                    With your OK, Google Analytics counts which pages and
                    features get used. It keeps a random ID in a cookie on this
                    device, never sees what you write, and is never used for
                    ads. Off unless you turn it on.
                  </p>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={analyticsOn}
                  aria-labelledby="analytics-switch-label"
                  aria-describedby="analytics-switch-help"
                  onClick={handleAnalyticsToggle}
                  className={`relative inline-flex h-7 w-12 flex-shrink-0 items-center rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-courtyard-clay ${
                    analyticsOn ? 'bg-courtyard-olive' : 'bg-courtyard-stone/40'
                  }`}
                >
                  <span
                    className={`inline-block h-5 w-5 rounded-full bg-white shadow transition-transform ${
                      analyticsOn ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
              <p className="text-sm text-courtyard-stone/80 mt-2" aria-live="polite">
                {analyticsOn ? 'On for this browser.' : 'Off for this browser.'}
              </p>
            </div>

            <p className="border-t border-courtyard-stone/10 pt-5 text-sm text-courtyard-stone/80">
              <a href={PRIVACY_URL} className="underline underline-offset-4 hover:text-courtyard-stone">
                Privacy Policy
              </a>
              <span aria-hidden="true"> · </span>
              <a href={TERMS_URL} className="underline underline-offset-4 hover:text-courtyard-stone">
                Terms of Use
              </a>
            </p>
          </Card>
        </motion.div>

        {/* Actions */}
        <motion.div
          className="space-y-3"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          {isAuthenticated && (
            <Button
              variant="secondary"
              className="w-full flex items-center justify-center gap-2"
              onClick={handleSignOut}
            >
              <LogOut className="w-4 h-4" aria-hidden="true" />
              Sign out
            </Button>
          )}

          {!showResetConfirm ? (
            <Button
              variant="ghost"
              className="w-full flex items-center justify-center gap-2 text-red-700"
              onClick={() => setShowResetConfirm(true)}
            >
              <RotateCcw className="w-4 h-4" aria-hidden="true" />
              Reset all progress
            </Button>
          ) : (
            <Card className="p-5 border-red-200 bg-red-50">
              <p className="text-sm text-red-800 mb-3">
                This erases your progress through the evenings, your XP,
                achievements and assessment results. Your saved toolkit and journal entries are
                kept. It cannot be undone.
              </p>
              <div className="flex gap-3">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowResetConfirm(false)}
                >
                  Cancel
                </Button>
                <button
                  onClick={handleResetProgress}
                  className="px-4 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 transition-colors"
                >
                  Yes, reset my progress
                </button>
              </div>
            </Card>
          )}
        </motion.div>
      </div>
    </>
  );
};

export default ProfilePage;
